import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CONTACT_INFO } from '../data';

export default function Home() {
  const [staff, setStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(true);

  useEffect(() => {
    // Fetch staff members for the home page preview
    fetch('https://api.dev.auxo.dk/public/content/staff')
      .then((res) => res.json())
      .then((data) => {
        // Limit to 4 key members for the home page
        setStaff(Array.isArray(data) ? data.slice(0, 4) : []);
        setLoadingStaff(false);
      })
      .catch(() => {
        setLoadingStaff(false);
      });
  }, []);

  return (
    <div className="bg-slate-50/30">
      {/* Hero Section */}
      <section className="relative bg-slate-900 overflow-hidden min-h-[600px] flex items-center">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/c39928d5-b2a6-4904-83a9-11ba7b5b852e/vester-bo-202014-20013.jpg"
            alt="Vester-Bo Omgivelser"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 text-white w-full text-left">
          <div className="max-w-2xl text-left items-start">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary/20 text-teal-300 border border-primary/30 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-teal-400 animate-pulse" />
              Social-psykiatrisk opholdssted siden 1999
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Trygge rammer, faglig udvikling og livskvalitet
            </h1>
            <p className="text-lg text-slate-200 mb-10 leading-relaxed">
              Vester-Bo er et social-psykiatrisk opholdssted i Holsted, der tilbyder professionel støtte og omsorg døgnet rundt for voksne med psykiske lidelser. Vi skaber et trygt hjem med plads til den enkelte.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/om-vester-bo"
                className="px-8 py-4 rounded-full text-white bg-primary hover:bg-primary-dark font-semibold shadow-lg hover:shadow-xl transition-all text-center"
              >
                Læs om os
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 rounded-full text-white bg-white/10 hover:bg-white/20 border border-white/20 font-semibold transition-all text-center"
              >
                Kontakt os
              </Link>
            </div>

            {/* Available Spots Notification Box inside Hero */}
            <div className="mt-8 p-4 sm:p-5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-teal-400/20 text-teal-300 flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-circle-info text-lg" />
                </div>
                <span className="font-semibold text-sm sm:text-base text-white">
                  Vi har ledige pladser - kontakt os for mere information
                </span>
              </div>
              <Link
                to="/contact"
                className="shrink-0 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold bg-primary hover:bg-primary-dark text-white transition-colors shadow-sm"
              >
                Kontakt os
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Welcome & Intro Section */}
      <section className="py-20 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-secondary">
                Velkommen til Vester-Bo
              </h2>
              <div className="h-1 w-20 bg-primary rounded" />
              <p className="text-slate-600 leading-relaxed text-lg">
                <strong>Vester-Bo</strong> er et social-psykiatrisk opholdssted, som er startet den 1. august 1999. Vi har plads til 7 beboere i vores hyggelige rammer i Holsted.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Vi tilbyder et opholdssted med personaledækning døgnet rundt for voksne mennesker (18-85 år) med psykiske lidelser som skizofreni, personlighedsforstyrrelse, affektive lidelser eller andre svære psykiske problemer.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Vester-Bo er et opholdssted, hvor man kan bo midlertidigt, indtil man er klar til at flytte i egen lejlighed eller anden støttemulighed. Beboeren skal dog være indstillet på et længerevarende ophold (min. 1 år). Vi kan også føre tilsyn ved psykisk syge, som bor i egen bolig.
              </p>
              <div className="pt-4">
                <Link
                  to="/om-vester-bo"
                  className="inline-flex items-center gap-2 text-primary hover:text-primary-dark font-semibold transition-colors"
                >
                  <span>Læs mere om vores målgruppe og formål</span>
                  <i className="fa-solid fa-arrow-right text-sm" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-teal-50 rounded-3xl -rotate-2 z-0" />
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/c39928d5-b2a6-4904-83a9-11ba7b5b852e/vester-bo-202014-20007.jpg"
                  alt="Vester-Bo Fællesskab"
                  className="relative z-10 rounded-2xl shadow-md w-full object-cover h-[350px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tilsynsrapport Section */}
      <section className="py-16 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-900 rounded-3xl p-8 sm:p-12 text-white relative overflow-hidden shadow-xl">
            <div className="absolute -right-10 -bottom-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
            <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-primary/20 text-teal-300 border border-primary/30 mb-4">
                  <i className="fa-solid fa-file-shield" />
                  <span>Kvalitet & Tilsyn</span>
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl font-bold mb-3">
                  Socialtilsynets Tilsynsrapport
                </h2>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Vester-Bo lever op til Socialtilsyn Syds høje krav til faglighed, tryghed og rammer. Du kan læse den fulde og nyeste tilsynsrapport for Vester-Bo her.
                </p>
              </div>
              <div className="shrink-0">
                <a
                  href={CONTACT_INFO.tilsynsrapportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-white bg-primary hover:bg-primary-dark font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  <i className="fa-regular fa-file-pdf text-xl" />
                  <span>Åben Tilsynsrapport (PDF)</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Offerings Grid */}
      <section className="py-20 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-secondary mb-4">
              Hvad tilbyder Vester-Bo?
            </h2>
            <p className="text-slate-600">
              Vi arbejder målrettet med den enkelte beboers ressourcer og behov for at skabe en struktureret og meningsfuld hverdag.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full">
              <div className="h-12 w-12 rounded-xl bg-teal-50 text-primary flex items-center justify-center text-xl mb-6">
                <i className="fa-solid fa-comments" />
              </div>
              <h3 className="font-serif text-xl font-bold text-secondary mb-3">
                Miljøterapi & Samtaler
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-grow">
                Vi tager udgangspunkt i den enkeltes ressourcer og arbejder kognitivt. Gennem samtaler lærer beboerne at sætte ord på tanker, følelser og handlinger.
              </p>
              <Link
                to="/milj-c3-b8terapi-og-samtaler"
                className="text-primary hover:text-primary-dark font-semibold text-sm inline-flex items-center gap-1.5"
              >
                <span>Læs mere</span>
                <i className="fa-solid fa-chevron-right text-xs" />
              </Link>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full">
              <div className="h-12 w-12 rounded-xl bg-teal-50 text-primary flex items-center justify-center text-xl mb-6">
                <i className="fa-solid fa-calendar-check" />
              </div>
              <h3 className="font-serif text-xl font-bold text-secondary mb-3">
                Faste holdepunkter
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-grow">
                En struktureret hverdag med fælles morgenmad, ADL-træning og medbestemmelse. Beboerne har medansvar for madlavning og daglige gøremål.
              </p>
              <Link
                to="/faste-holdepunkter-i-l-c3-b8bet-af-dagen"
                className="text-primary hover:text-primary-dark font-semibold text-sm inline-flex items-center gap-1.5"
              >
                <span>Læs mere</span>
                <i className="fa-solid fa-chevron-right text-xs" />
              </Link>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full">
              <div className="h-12 w-12 rounded-xl bg-teal-50 text-primary flex items-center justify-center text-xl mb-6">
                <i className="fa-solid fa-van-shuttle" />
              </div>
              <h3 className="font-serif text-xl font-bold text-secondary mb-3">
                Udadrettede aktiviteter
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-grow">
                Vi tager ud af huset til sport, udflugter og ferier. Det styrker de sociale færdigheder og giver gode oplevelser at se frem til og mindes.
              </p>
              <Link
                to="/udadrettede-aktiviteter"
                className="text-primary hover:text-primary-dark font-semibold text-sm inline-flex items-center gap-1.5"
              >
                <span>Læs mere</span>
                <i className="fa-solid fa-chevron-right text-xs" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Physical Environment Section */}
      <section className="py-20 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 order-last lg:order-first">
              <div className="relative">
                <div className="absolute -inset-4 bg-slate-100 rounded-3xl rotate-2 z-0" />
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/c39928d5-b2a6-4904-83a9-11ba7b5b852e/fysiske-rammer.jpg"
                  alt="Vester-Bo Fysiske Rammer"
                  className="relative z-10 rounded-2xl shadow-md w-full object-cover h-[380px]"
                />
              </div>
            </div>

            <div className="lg:col-span-7 space-y-6">
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-secondary">
                De fysiske rammer
              </h2>
              <div className="h-1 w-20 bg-primary rounded" />
              <p className="text-slate-600 leading-relaxed">
                Ejendommen er beliggende i et hyggeligt lokalsamfund i Holsted By, Vejen kommune. Grundarealet er på 1094 m² fordelt på beboelse, en dejlig have og indkørsel.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Beboelsen på 305 m² består af to bygninger – <strong>hovedbygningen</strong> og <strong>annekset</strong>, som ligger kun 3 meter fra hinanden. Dette giver en god balance mellem fællesskab i hovedbygningen og privatliv på eget værelse.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-lg bg-teal-50 text-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="fa-solid fa-house-chimney text-sm" />
                  </div>
                  <div>
                    <h4 className="font-serif font-bold text-secondary text-sm">Hovedbygningen</h4>
                    <p className="text-xs text-slate-500">Køkken/alrum, stuer, kontor og en 1-værelses lejlighed.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-lg bg-teal-50 text-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="fa-solid fa-building text-sm" />
                  </div>
                  <div>
                    <h4 className="font-serif font-bold text-secondary text-sm">Annekset</h4>
                    <p className="text-xs text-slate-500">6 lyse værelser på 14 m² med internet og badeværelser.</p>
                  </div>
                </div>
              </div>
              <div className="pt-4">
                <Link
                  to="/fysiske-rammer"
                  className="px-6 py-3 rounded-full text-white bg-secondary hover:bg-secondary-dark font-semibold shadow-md transition-colors inline-block"
                >
                  Se de fysiske rammer
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vacancy Status Banner */}
      <section className="py-16 bg-teal-50/50 border-y border-teal-100/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold bg-primary text-white mb-4 shadow-sm">
            <i className="fa-solid fa-circle-info" />
            <span>Pladsstatus</span>
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-secondary mb-4">
            Søger du eller en borger en plads?
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto mb-8">
            Vi har løbende visitationer. Du er altid velkommen til at kontakte os for at høre om den aktuelle belægning eller for at aftale et uforpligtende forbesøg.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/ledige-pladser"
              className="px-6 py-3 rounded-full text-white bg-primary hover:bg-primary-dark font-semibold shadow-md transition-colors"
            >
              Se ledige pladser
            </Link>
            <a
              href={`tel:${CONTACT_INFO.phone}`}
              className="px-6 py-3 rounded-full text-secondary bg-white hover:bg-slate-50 border border-slate-200 font-semibold shadow-sm transition-colors"
            >
              Ring til os: {CONTACT_INFO.phone}
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
